import React from 'react';
import { ShieldCheck, Lock, ScrollText, FileCheck2, Scale, Database, Cpu, LineChart, BookOpen, KeyRound } from 'lucide-react';

/**
 * Category-aware visual for practical courses. Renders a layered icon
 * composition on a themed gradient backdrop when no provider logo exists.
 *
 * Categories detected (case-insensitive substring match):
 *   - "privacy"       → Shield + Lock (blue → cyan)
 *   - "governance"    → ScrollText + FileCheck (indigo → violet)
 *   - "compliance"    → Scale + FileCheck (amber → orange)
 *   - "data"          → Database + LineChart (emerald → teal)
 *   - "security"      → ShieldCheck + KeyRound (slate → sky)
 *   - "tech" / "ai"   → Cpu + LineChart (fuchsia → purple)
 *   - default         → BookOpen (primary)
 */
const THEMES = {
    privacy: {
        gradient: 'from-sky-500 via-blue-500 to-cyan-500',
        pattern: 'from-blue-400/30 via-transparent to-cyan-300/20',
        primaryIcon: ShieldCheck,
        accentIcon: Lock,
        label: 'Privacy',
    },
    governance: {
        gradient: 'from-indigo-500 via-violet-500 to-fuchsia-500',
        pattern: 'from-violet-400/30 via-transparent to-fuchsia-300/20',
        primaryIcon: ScrollText,
        accentIcon: FileCheck2,
        label: 'Governance',
    },
    compliance: {
        gradient: 'from-amber-500 via-orange-500 to-rose-500',
        pattern: 'from-amber-400/30 via-transparent to-rose-300/20',
        primaryIcon: Scale,
        accentIcon: FileCheck2,
        label: 'Compliance',
    },
    data: {
        gradient: 'from-emerald-500 via-teal-500 to-cyan-500',
        pattern: 'from-emerald-400/30 via-transparent to-cyan-300/20',
        primaryIcon: Database,
        accentIcon: LineChart,
        label: 'Data',
    },
    security: {
        gradient: 'from-slate-700 via-slate-600 to-sky-600',
        pattern: 'from-sky-400/30 via-transparent to-slate-400/10',
        primaryIcon: ShieldCheck,
        accentIcon: KeyRound,
        label: 'Security',
    },
    tech: {
        gradient: 'from-fuchsia-500 via-purple-500 to-indigo-500',
        pattern: 'from-fuchsia-400/30 via-transparent to-indigo-300/20',
        primaryIcon: Cpu,
        accentIcon: LineChart,
        label: 'Tech',
    },
    default: {
        gradient: 'from-primary-500 via-primary-600 to-violet-500',
        pattern: 'from-primary-400/30 via-transparent to-violet-300/20',
        primaryIcon: BookOpen,
        accentIcon: FileCheck2,
        label: 'Course',
    },
};

export const getCourseTheme = (category = '') => {
    const c = (category || '').toLowerCase();
    if (c.includes('privacy')) return THEMES.privacy;
    if (c.includes('governance')) return THEMES.governance;
    if (c.includes('compliance') || c.includes('legal')) return THEMES.compliance;
    if (c.includes('security') || c.includes('cyber')) return THEMES.security;
    if (c.includes('data') || c.includes('analytics')) return THEMES.data;
    if (c.includes('tech') || c.includes('ai') || c.includes('engineer') || c.includes('develop')) return THEMES.tech;
    return THEMES.default;
};

/**
 * size: 'card' (h-40, for grid) | 'hero' (fills a rounded square in the detail hero)
 */
export const CourseVisual = ({ category, size = 'card', className = '' }) => {
    const theme = getCourseTheme(category);
    const Primary = theme.primaryIcon;
    const Accent = theme.accentIcon;

    if (size === 'hero') {
        return (
            <div className={`relative w-full h-full overflow-hidden rounded-2xl bg-gradient-to-br ${theme.gradient} ${className}`}>
                {/* Grid pattern */}
                <div
                    className="absolute inset-0 opacity-30"
                    style={{
                        backgroundImage: 'linear-gradient(rgba(255,255,255,0.15) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.15) 1px, transparent 1px)',
                        backgroundSize: '14px 14px',
                    }}
                />
                {/* Soft radial glow */}
                <div className={`absolute inset-0 bg-gradient-to-tr ${theme.pattern}`} />
                {/* Accent circle */}
                <div className="absolute -top-6 -right-6 w-24 h-24 rounded-full bg-white/10 blur-xl" />
                <div className="absolute -bottom-8 -left-8 w-28 h-28 rounded-full bg-white/10 blur-2xl" />
                {/* Icon stack */}
                <div className="absolute inset-0 flex items-center justify-center">
                    <div className="relative">
                        <Primary className="w-14 h-14 text-white drop-shadow-lg" strokeWidth={1.6} />
                        <div className="absolute -bottom-2 -right-2 w-7 h-7 rounded-lg bg-white/90 flex items-center justify-center shadow-lg">
                            <Accent className="w-4 h-4 text-neutral-800" strokeWidth={2.2} />
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Card variant (h-40)
    return (
        <div className={`relative h-40 overflow-hidden bg-gradient-to-br ${theme.gradient} ${className}`}>
            {/* Grid pattern */}
            <div
                className="absolute inset-0 opacity-25"
                style={{
                    backgroundImage: 'linear-gradient(rgba(255,255,255,0.2) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.2) 1px, transparent 1px)',
                    backgroundSize: '18px 18px',
                }}
            />
            {/* Radial soft glow */}
            <div className={`absolute inset-0 bg-gradient-to-tr ${theme.pattern}`} />
            {/* Big soft blobs */}
            <div className="absolute -top-10 -right-10 w-32 h-32 rounded-full bg-white/10 blur-2xl" />
            <div className="absolute -bottom-14 -left-10 w-40 h-40 rounded-full bg-white/10 blur-3xl" />
            {/* Icon stack */}
            <div className="absolute inset-0 flex items-center justify-center">
                <div className="relative">
                    <div className="absolute inset-0 rounded-2xl bg-white/10 backdrop-blur-sm scale-110" />
                    <div className="relative flex items-center justify-center w-20 h-20 rounded-2xl bg-white/15 backdrop-blur-md border border-white/20">
                        <Primary className="w-10 h-10 text-white" strokeWidth={1.6} />
                    </div>
                    <div className="absolute -bottom-2 -right-2 w-8 h-8 rounded-xl bg-white flex items-center justify-center shadow-lg ring-2 ring-white/40">
                        <Accent className="w-4 h-4 text-neutral-800" strokeWidth={2.2} />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CourseVisual;
